/**
 * Knowledge Graphs Tab
 *
 * Lists and manages knowledge graphs with filtering
 */
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { dataStore } from '@/stores/DataStore'
import { GraphType, GraphStatus } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

const KnowledgeGraphsTab: React.FC = observer(() => {
  const navigate = useNavigate()
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    const projectId = dataStore.selectedProjectId
    knowledgeGraphStore.fetchGraphs(projectId || undefined)
  }, [dataStore.selectedProjectId])

  const handleCreateGraph = () => {
    navigate('/knowledge-graphs/new')
  }

  const handleGraphClick = (graphId: string) => {
    navigate(`/knowledge-graphs/${graphId}`)
  }

  const handleEditGraph = (graphId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/knowledge-graphs/${graphId}/edit`)
  }

  const handleDeleteGraph = async (graphId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (!confirm('Are you sure you want to delete this graph? This action cannot be undone.')) {
      return
    }

    try {
      await knowledgeGraphStore.deleteGraph(graphId)
      toast.success('Graph deleted successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete graph')
    }
  }

  const getStatusBadgeVariant = (status: GraphStatus): 'success' | 'warning' | 'gray' => {
    switch (status) {
      case GraphStatus.ACTIVE:
        return 'success'
      case GraphStatus.DRAFT:
        return 'warning'
      case GraphStatus.ARCHIVED:
        return 'gray'
      default:
        return 'gray'
    }
  }

  const getGraphTypeBadgeVariant = (
    type: GraphType
  ): 'primary' | 'success' | 'warning' | 'error' | 'gray' => {
    switch (type) {
      case GraphType.ONTOLOGY:
        return 'primary'
      case GraphType.SEMANTIC:
        return 'primary'
      case GraphType.CAUSAL:
        return 'error'
      case GraphType.TEMPORAL:
        return 'warning'
      case GraphType.HIERARCHICAL:
        return 'success'
      case GraphType.NETWORK:
        return 'primary'
      case GraphType.WORKFLOW:
        return 'primary'
      case GraphType.AGENT_MEMORY:
        return 'warning'
      default:
        return 'gray'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-gray-600">
          {knowledgeGraphStore.totalGraphs} total graphs
          {dataStore.selectedProjectId && ' (filtered by project)'}
        </p>
        <Button variant="primary" onClick={handleCreateGraph} size="sm">
          <PlusIcon className="w-5 h-5 mr-2" />
          New Graph
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={knowledgeGraphStore.searchQuery}
                  onChange={(e) => knowledgeGraphStore.setSearchQuery(e.target.value)}
                  placeholder="Search graphs by name or description..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <FunnelIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Filters</span>
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-gray-200">
              <select
                value={knowledgeGraphStore.graphTypeFilter}
                onChange={(e) =>
                  knowledgeGraphStore.setGraphTypeFilter(e.target.value as GraphType | 'all')
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              >
                <option value="all">All Types</option>
                {Object.values(GraphType).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>

              <select
                value={knowledgeGraphStore.statusFilter}
                onChange={(e) =>
                  knowledgeGraphStore.setStatusFilter(e.target.value as GraphStatus | 'all')
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              >
                <option value="all">All Statuses</option>
                {Object.values(GraphStatus).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              <Button variant="ghost" size="sm" onClick={() => knowledgeGraphStore.clearFilters()}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Loading State */}
      {knowledgeGraphStore.isLoading ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading graphs...</p>
        </Card>
      ) : knowledgeGraphStore.error ? (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <p className="text-red-600">{knowledgeGraphStore.error}</p>
            <Button variant="ghost" size="sm" onClick={() => knowledgeGraphStore.clearError()}>
              Dismiss
            </Button>
          </div>
        </Card>
      ) : knowledgeGraphStore.filteredGraphs.length === 0 ? (
        <Card className="text-center py-12">
          <ChartBarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {knowledgeGraphStore.searchQuery ||
            knowledgeGraphStore.graphTypeFilter !== 'all' ||
            knowledgeGraphStore.statusFilter !== 'all'
              ? 'No graphs match your filters'
              : 'No knowledge graphs yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {knowledgeGraphStore.searchQuery ||
            knowledgeGraphStore.graphTypeFilter !== 'all' ||
            knowledgeGraphStore.statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first knowledge graph to get started'}
          </p>
          {!knowledgeGraphStore.searchQuery &&
            knowledgeGraphStore.graphTypeFilter === 'all' &&
            knowledgeGraphStore.statusFilter === 'all' && (
              <Button variant="primary" onClick={handleCreateGraph}>
                <PlusIcon className="w-5 h-5 mr-2" />
                New Graph
              </Button>
            )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {knowledgeGraphStore.filteredGraphs.map((graph) => (
            <Card
              key={graph.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleGraphClick(graph.id)}
            >
              <div className="flex items-center justify-between mb-3">
                <Badge variant={getGraphTypeBadgeVariant(graph.graph_type)}>
                  {graph.graph_type}
                </Badge>
                <Badge variant={getStatusBadgeVariant(graph.status)}>{graph.status}</Badge>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">{graph.name}</h3>

              {graph.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{graph.description}</p>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{graph.node_count} nodes</span>
                <span>{graph.edge_count} edges</span>
              </div>

              {graph.tags && graph.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {graph.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                  {graph.tags.length > 3 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      +{graph.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}

              <div className="text-xs text-gray-500 mb-4">
                Updated {formatDistanceToNow(new Date(graph.updated_at), { addSuffix: true })}
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => handleEditGraph(graph.id, e)}
                  className="flex-1"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={(e) => handleDeleteGraph(graph.id, e)}
                  className="flex-1"
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {knowledgeGraphStore.totalGraphs > knowledgeGraphStore.pageSize && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {Math.min(knowledgeGraphStore.pageSize, knowledgeGraphStore.totalGraphs)} of{' '}
              {knowledgeGraphStore.totalGraphs} graphs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => knowledgeGraphStore.setPage(knowledgeGraphStore.currentPage - 1)}
                disabled={knowledgeGraphStore.currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-900">
                Page {knowledgeGraphStore.currentPage} of{' '}
                {Math.ceil(knowledgeGraphStore.totalGraphs / knowledgeGraphStore.pageSize)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => knowledgeGraphStore.setPage(knowledgeGraphStore.currentPage + 1)}
                disabled={
                  knowledgeGraphStore.currentPage * knowledgeGraphStore.pageSize >=
                  knowledgeGraphStore.totalGraphs
                }
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
})

KnowledgeGraphsTab.displayName = 'KnowledgeGraphsTab'

export default KnowledgeGraphsTab
